import { type FC, type ReactNode, type RefObject, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { PipeSeparator } from './PipeSeparator.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { FormattedPrice } from 'dibs-buyer-price/exports/FormattedPrice.ts';
import { useVisibilityTracker } from 'dibs-visibility-tracker/src/useVisibilityTracker.ts';

import { type ProductPrice_item$key } from './__generated__/ProductPrice_item.graphql.ts';

import dibsCss from 'dibs-css';
import { useImproveSalePriceDisplayAbTest } from '../hooks/useImproveSalePriceDisplayAbTest.ts';

export const TYPES = {
    SALE: 'WEEKLY_SALE',
    NET: 'NET',
    RETAIL: 'RETAIL',
    SOLD: 'SOLD',
    HOLD: 'HOLD',
    UNAVAILABLE: 'UNAVAILABLE',
    PUR: 'PUR',
    SOLD_PRICE: 'SOLD_PRICE',
} as const;

const TEXT_PRICE_TYPES = new Set<string>([TYPES.SOLD, TYPES.HOLD, TYPES.UNAVAILABLE, TYPES.PUR]);

const messages = {
    saleLabel: <FormattedMessage id="dc.buyProductTile.salePrice" defaultMessage="Sale Price" />,
    netLabel: <FormattedMessage id="dc.buyProductTile.netPrice" defaultMessage="Net Price" />,
    retailLabel: <FormattedMessage id="dc.buyProductTile.listPrice" defaultMessage="List Price" />,
    soldPriceLabel: (
        <FormattedMessage id="dc.buyProductTile.similarSoldPrice" defaultMessage="Sold Price" />
    ),
    priceVariabilityLabel: (
        <FormattedMessage defaultMessage="From " id="dc.buyerProductTile.priceVariability" />
    ),
    textPercentageOff: (percentageOff: number | string): ReactNode => (
        <FormattedMessage
            id="dc.buyProductTile.percentageOff"
            defaultMessage={`{percentageOff}% Off`}
            values={{ percentageOff }}
        />
    ),
    textPriceValue: (textPriceType: string): ReactNode => (
        <FormattedMessage
            values={{ textPriceType }}
            id="dc.searchProductTile.textPriceValue"
            defaultMessage={`{textPriceType, select,
            SOLD {Sold}
            UNAVAILABLE {Unavailable}
            PUR {Price Upon Request}
            HOLD {On Hold}
            other {Sold}
        }`}
        />
    ),
};

const PriceComponentWrapper: FC<{
    children: ReactNode[];
    dataTn: string;
    ref: RefObject<HTMLParagraphElement | null>;
    isSmall?: boolean;
    canShowDimensions?: boolean;
    shouldImproveSalePriceDisplay?: boolean;
}> = ({ children, dataTn, ref, isSmall, canShowDimensions, shouldImproveSalePriceDisplay }) => {
    const isPriceOneLine = isSmall && canShowDimensions;
    const isPriceTwoLines = !isPriceOneLine && shouldImproveSalePriceDisplay;
    return (
        <p
            data-tn={dataTn}
            ref={ref}
            className={classnames(
                dibsCss.textBodySmallNormal,
                dibsCss.mtXxsmall,
                dibsCss.whitespaceBreakSpaces,
                {
                    [dibsCss.lineClamp1]: isPriceOneLine,
                    [dibsCss.lineClamp2]: isPriceTwoLines,
                }
            )}
        >
            {children}
        </p>
    );
};
const Price: FC<{
    price: number;
    currency: string;
    showPriceVariability: boolean | null | undefined;
}> = ({ price, currency, showPriceVariability }) => (
    <strong data-tn="price" className={dibsCss.textBodySmallHeavy}>
        {showPriceVariability ? messages.priceVariabilityLabel : null}
        <FormattedPrice value={price} currency={currency} />
    </strong>
);

const Discount: FC<{ percentageOff: string | null | undefined; netPrice?: boolean }> = ({
    percentageOff,
    netPrice,
}) =>
    percentageOff ? (
        <span
            data-tn="discount-amount"
            className={classnames(
                netPrice ? dibsCss.textTradeprimary : dibsCss.textAlertsecondary,
                dibsCss.flexNowrap
            )}
        >
            {messages.textPercentageOff(percentageOff)}
        </span>
    ) : null;

const Label: FC<{ children: ReactNode; showLabel?: boolean | null }> = ({
    children,
    showLabel = true,
}) => (showLabel ? <span className={dibsCss.mlXsmall}>{children}</span> : null);

const itemFragment = graphql`
    fragment ProductPrice_item on Item
    @argumentDefinitions(
        isTrade: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        priceBookName: { type: "String" }
    ) {
        displayPrice(page: $pageDisplayEnum, isTrade: $isTrade, priceBookName: $priceBookName) {
            convertedAmountList {
                amount
                currency
            }
            originalConvertedAmountList {
                amount
                currency
            }
            quantityDisplay
            percentageOff
            textType
            amountType
            showPriceLabel
            showPriceVariability
            ...useImproveSalePriceDisplayAbTest_displayPrice
        }
    }
`;

type Props = {
    currency: string;
    item: ProductPrice_item$key;
    hideRecentSoldPrice?: boolean;
    isSmall?: boolean;
    canShowDimensions?: boolean;
};

export const ProductPrice: FC<Props> = ({
    item: itemRef,
    currency,
    hideRecentSoldPrice = false,
    isSmall,
    canShowDimensions,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const displayPrice = item?.displayPrice?.[0];

    const {
        textType,
        amountType,
        convertedAmountList,
        originalConvertedAmountList,
        quantityDisplay,
        percentageOff,
        showPriceLabel,
        showPriceVariability,
    } = displayPrice || {};

    const price = Math.floor(convertedAmountList?.find(c => c?.currency === currency)?.amount || 0);
    const originalPrice = Math.floor(
        originalConvertedAmountList?.find(c => c?.currency === currency)?.amount || 0
    );

    const { shouldImproveSalePriceDisplay, trackImproveSalePriceDisplayAbTest } =
        useImproveSalePriceDisplayAbTest({ displayPrice });
    const priceRef = useRef<HTMLParagraphElement>(null);
    useVisibilityTracker({
        elementRef: priceRef,
        onVisibilityChange: ({ isVisible }) => {
            if (isVisible) {
                trackImproveSalePriceDisplayAbTest();
            }
        },
    });
    const quantityDisplayCopy = typeof quantityDisplay === 'string' ? ` ${quantityDisplay}` : null;

    if (typeof textType === 'string' && TEXT_PRICE_TYPES.has(textType)) {
        if (textType === TYPES.PUR) {
            return (
                <p
                    className={classnames(
                        dibsCss.textBodySmallNormal,
                        dibsCss.textNoir,
                        dibsCss.mtXxsmall
                    )}
                >
                    {messages.textPriceValue(TYPES.PUR)}
                </p>
            );
        }
        const showPrice = !hideRecentSoldPrice && price > 0;
        return (
            <p
                className={classnames(
                    dibsCss.mtXxsmall,
                    dibsCss.flex,
                    dibsCss.flexWrap,
                    dibsCss.itemsCenter
                )}
                data-tn={`price-${textType}`}
            >
                <span
                    className={classnames(
                        dibsCss.textAlertsecondary,
                        dibsCss.textBodySmallHeavy,
                        dibsCss.whitespaceNowrap
                    )}
                >
                    {messages.textPriceValue(textType)}
                </span>
                {showPrice && (
                    <>
                        <PipeSeparator />
                        <span data-tn="price-retail" className={dibsCss.textBodySmallNormal}>
                            <del data-tn="price">
                                <FormattedPrice value={price} currency={currency} />
                                {quantityDisplayCopy}
                            </del>
                        </span>
                    </>
                )}
            </p>
        );
    }

    if (!price || !amountType) {
        return null;
    }

    const wrapperProps = {
        isSmall,
        canShowDimensions,
        ref: priceRef,
        shouldImproveSalePriceDisplay,
    };
    if (amountType === TYPES.SALE) {
        return (
            <PriceComponentWrapper dataTn="price-sale" {...wrapperProps}>
                <Price
                    price={price}
                    currency={currency}
                    showPriceVariability={showPriceVariability}
                />
                {shouldImproveSalePriceDisplay ? (
                    <>
                        {quantityDisplayCopy}
                        <del className={classnames(dibsCss.mxXsmall, dibsCss.textSatan)}>
                            <FormattedPrice value={originalPrice} currency={currency} />
                        </del>
                        <Discount percentageOff={percentageOff} />
                    </>
                ) : (
                    <>
                        <Label showLabel={!isSmall}>{messages.saleLabel}</Label>
                        {quantityDisplayCopy}
                        {percentageOff && <PipeSeparator />}
                        <Discount percentageOff={percentageOff} />
                    </>
                )}
            </PriceComponentWrapper>
        );
    }

    if (amountType === TYPES.NET) {
        return (
            <PriceComponentWrapper dataTn="price-net" {...wrapperProps}>
                <Price
                    price={price}
                    currency={currency}
                    showPriceVariability={showPriceVariability}
                />
                {shouldImproveSalePriceDisplay ? (
                    <>
                        {quantityDisplayCopy}
                        <del className={classnames(dibsCss.mxXsmall, dibsCss.textSatan)}>
                            <FormattedPrice value={originalPrice} currency={currency} />
                        </del>
                        <Discount percentageOff={percentageOff} netPrice />
                    </>
                ) : (
                    <>
                        <Label showLabel={!isSmall}>{messages.netLabel}</Label>
                        {quantityDisplayCopy}
                        {percentageOff && <PipeSeparator />}
                        <Discount percentageOff={percentageOff} netPrice />
                    </>
                )}
            </PriceComponentWrapper>
        );
    }

    if (amountType === TYPES.RETAIL) {
        if (textType === TYPES.SOLD_PRICE) {
            return (
                <PriceComponentWrapper dataTn="price-similar-sold" {...wrapperProps}>
                    <Price
                        price={price}
                        currency={currency}
                        showPriceVariability={showPriceVariability}
                    />
                    <Label>{messages.soldPriceLabel}</Label>
                    {quantityDisplayCopy}
                </PriceComponentWrapper>
            );
        }
        return (
            <PriceComponentWrapper dataTn="price-retail" {...wrapperProps}>
                <Price
                    price={price}
                    currency={currency}
                    showPriceVariability={showPriceVariability}
                />
                <Label showLabel={showPriceLabel}>{messages.retailLabel}</Label>
                {quantityDisplayCopy}
            </PriceComponentWrapper>
        );
    }

    return null;
};
